import { useCallback, useEffect, useRef, useState } from 'react';
import { ImageMetadata } from '../dto/EntityRepository';
import { debounce } from '../util/debounce';
import {
  convertToJsonMetadata,
  generateOptimizedUrl,
  getFallbackDeviceType,
  isBase64,
  isDeviceAvailable,
} from '../util/findAdjustImg';

export function useOptimizedBackgroundUrl(backgroundImg: string, enabled: boolean) {
  const [metadata, setMetadata] = useState<ImageMetadata | null>(null);
  const [metadataFetched, setMetadataFetched] = useState(false);
  const [browserType, setBrowserType] = useState<string | null>(null);
  const [adjustedUrl, setAdjustedUrl] = useState<string | null>(null);
  const currentDeviceTypeRef = useRef<string | null>(null);
  const previousAdjustedUrlRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isHttpUrl = Boolean(backgroundImg && backgroundImg.startsWith('http'));
  const isBase64Img = isBase64(backgroundImg);

  useEffect(() => {
    if (!enabled) {
      setMetadata(null);
      setMetadataFetched(false);
      setBrowserType(null);
      currentDeviceTypeRef.current = null;
      previousAdjustedUrlRef.current = null;
      setAdjustedUrl(null);
      return;
    }

    const fetchMetadata = async () => {
      try {
        const jsonUrl = backgroundImg.replace(
          /\/[^/]+\.(?:jpeg|jpg|png|gif|webp)$/,
          '/meta.json',
        );
        const response = await fetch(jsonUrl, { cache: 'no-cache' });
        if (!response.ok) {
          throw new Error('Failed to fetch metadata');
        }
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          const imageMetadata = convertToJsonMetadata(data);
          setMetadata(imageMetadata);
          setMetadataFetched(true);

          if (containerRef.current) {
            const containerWidth =
              containerRef.current.offsetWidth ||
              containerRef.current.getBoundingClientRect().width;
            const deviceType = getFallbackDeviceType(imageMetadata, containerWidth);
            if (currentDeviceTypeRef.current !== deviceType) {
              setBrowserType(deviceType);
              currentDeviceTypeRef.current = deviceType;
            }
          }
        } else {
          throw new Error('Response is not JSON');
        }
      } catch {
        setMetadata(null);
        setMetadataFetched(true);
      }
    };

    if (backgroundImg && !isBase64Img && isHttpUrl) {
      setMetadataFetched(false);
      setBrowserType(null);
      currentDeviceTypeRef.current = null;
      fetchMetadata();
    } else {
      setMetadata(null);
      setMetadataFetched(true);
    }
  }, [backgroundImg, enabled, isBase64Img, isHttpUrl]);

  useEffect(() => {
    if (!enabled || !containerRef.current || !metadata || isBase64Img || !isHttpUrl) {
      return;
    }

    const debouncedCallback = debounce((entry: ResizeObserverEntry) => {
      const width = entry.contentRect.width;
      const newDeviceType = getFallbackDeviceType(metadata, width);
      if (currentDeviceTypeRef.current !== newDeviceType) {
        setBrowserType(newDeviceType);
        currentDeviceTypeRef.current = newDeviceType;
      }
    }, 200);

    const observer = new ResizeObserver(([entry]) => {
      debouncedCallback(entry);
    });

    observer.observe(containerRef.current);

    const initialWidth =
      containerRef.current.offsetWidth ||
      containerRef.current.getBoundingClientRect().width;
    const initialDeviceType = getFallbackDeviceType(metadata, initialWidth);
    if (currentDeviceTypeRef.current !== initialDeviceType) {
      setBrowserType(initialDeviceType);
      currentDeviceTypeRef.current = initialDeviceType;
    }

    return () => {
      observer.disconnect();
      debouncedCallback.cancel();
    };
  }, [enabled, metadata, backgroundImg, isBase64Img, isHttpUrl]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let newUrl: string | null = null;

    if (isBase64Img || !isHttpUrl) {
      newUrl = backgroundImg;
    } else if (!metadataFetched) {
      newUrl = null;
    } else if (!metadata) {
      newUrl = backgroundImg;
    } else if (
      backgroundImg &&
      metadata &&
      browserType &&
      isDeviceAvailable(metadata, browserType)
    ) {
      newUrl = generateOptimizedUrl(backgroundImg, browserType, metadata);
    } else {
      newUrl = backgroundImg;
    }

    if (previousAdjustedUrlRef.current !== newUrl) {
      setAdjustedUrl(newUrl);
      previousAdjustedUrlRef.current = newUrl;
    }
  }, [enabled, browserType, backgroundImg, metadata, metadataFetched, isBase64Img, isHttpUrl]);

  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
  }, []);

  return {
    displayUrl: enabled ? adjustedUrl : backgroundImg,
    containerRef: setContainerRef,
  };
}
